import React, {useEffect, useRef, useState} from "react";
import {InputNumber} from "primereact/inputnumber";
import {Chips} from "primereact/chips";
import {Checkbox} from "primereact/checkbox";
import {Tag} from 'primereact/tag';
import InfoLabel from "./InfoLabel";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {FileUpload, FileUploadHandlerParam, FileUploadHeaderTemplateOptions} from "primereact/fileupload";
import {Toast} from "primereact/toast";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {ScheduledTasksApi} from "api/ScheduledTasksApi";
import {restApiRoot} from "utils/utils";

import {MultiSelect, MultiSelectChangeParams} from "primereact/multiselect";
import {downloadBase64AsFile} from "utils/file-utils";
import {DropdownItem} from "utils/general-models";
import {DatasetDropdown} from "components/basic/dropdowns/dataset/DatasetDropdown";
import {DatasetCategoryDropdown} from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import Editor from "@monaco-editor/react";
import {
    IProductTemplateDropdownChangeParams,
    ProductTemplateDropdown
} from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import {DatasetMultiSelect} from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import ProductTemplateTitle from "components/basic/display-titles/ProductTemplateTitle";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import {useGetAirflowDagTaskInfos} from "api/api-hooks/airflow-hooks";
import {ScheduledTaskDTO} from "../../../../api/model/airflow-task/ScheduledTaskDTO";

import {TaskTemplateInputSubType} from "../../../../api/model/enums";
import {
    ProductTemplateTaskSelectionOptions
} from "../../../../api/model/product-template/ProductTemplateTaskSelectionOptions";
import {ITaskTemplateInputOptions} from "../../../../api/model/airflow-task/task-template/TaskTemplateInfo";

const chronoUnits = [
    {
        value: "SECONDS",
        label: "Seconds"
    },
    {
        value: "MINUTES",
        label: "Minutes"
    },
    {
        value: "HOURS",
        label: "Hours"
    },
    {
        value: "DAYS",
        label: "Days"
    },
    {
        value: "WEEKS",
        label: "Weeks"
    },
    {
        value: "MONTHS",
        label: "Months"
    },
];

interface InputChangeEventBase {
    property?: string;
}

export interface InputChangeEvent<T> extends InputChangeEventBase {
    value: T;
}

export interface IScheduledTaskPropertyRef {
    property: keyof ScheduledTaskDTO | string;
}

export interface IFieldInputProps extends IScheduledTaskPropertyRef {
    label: string;
    disabled?: boolean;
    info?: string;
    width?: number;
    widthMd?: number;
    fieldClass?: string;
}

export interface ICommentFieldInputProps extends IFieldInputProps {
    content: string,
}

export interface IBaseFieldInputProps extends IFieldInputProps {
    children: JSX.Element
    fieldClass?: string
}

export interface ITextFieldInputProps extends IFieldInputProps {
    value?: string | null;
    default?: string;
    onChange?: (e: InputChangeEvent<string>) => void;
}

export interface INumberFieldInputProps extends IFieldInputProps {
    value?: number | null;
    default?: number;
    onChange?: (e: InputChangeEvent<number | undefined>) => void;
    decimalPlaces?: number
    suffix?: string;
}

export interface IChipsFieldInputProps extends IFieldInputProps {
    value: string[] | null | undefined;
    default?: string[];
    onChange?: (e: InputChangeEvent<any>) => void;
}

export interface IBooleanFieldInputProps extends IFieldInputProps {
    value?: boolean | null;
    default?: boolean;
    onChange?: (e: InputChangeEvent<boolean>) => void;
}

export interface IDropdownFieldInputProps extends IFieldInputProps {
    value?: string | null;
    default?: string;
    onChange?: (e: InputChangeEvent<any>) => void;
    options?: any[],
    editable?:boolean
}

export interface IDatasetDropdownInputProps extends IFieldInputProps {
    value?: string | null | undefined;
    onChange?: (e: InputChangeEvent<string|null>) => void;
}

export interface IDatasetMultiSelect extends IFieldInputProps {
    value?: string[] | null | undefined;
    onChange?: (e: InputChangeEvent<string[]>) => void;
}

export interface IDatasetCategoryDropdownInputProps extends IFieldInputProps {
    value?: number | null | undefined;
    onChange?: (e: InputChangeEvent<number>) => void;
}

export interface ITaskConfigProductTemplateFieldProps extends IFieldInputProps {
    value?: ProductTemplateTaskSelectionOptions | null | undefined;
    onChange?: (e: InputChangeEvent<ProductTemplateTaskSelectionOptions>) => void;
}

export interface ITaskConfigFreestyleJsonFieldProps extends IFieldInputProps {
    value?: any | null | undefined;
    onChange?: (e: InputChangeEvent<any>) => void;
}

export interface IChronoSpanInputProps extends IFieldInputProps {
    propertyUnit: string;
    value: number | null | undefined;
    valueUnit: string | null | undefined;
    default?: number;
    defaultUnit?: string;
    onChange?: (e: InputChangeEvent<number | string>) => void;
}

export interface IPythonCodeFile {
    id: number;
    name: string;
    code: string;
    codeB64: string;
}

interface IFileInputProps {
    value: number | undefined | null;
    initialCode: string | null | undefined;
    codeB64: string | null | undefined;
    initialFilename: string | null | undefined;
    onChange: (e: IPythonCodeFile) => void;
}

interface IAirflowDepDropdownProps extends IFieldInputProps {
    dagId: string;
    value: string[] | undefined;
    onChange: (e: InputChangeEvent<string>) => void;
}

export interface ITaskConfigConstantFieldProps extends IFieldInputProps {
    value?: any;
    onChange?: (e: InputChangeEvent<any>) => void;
    options?: ITaskTemplateInputOptions;
    unit?: string;
}


function BaseFieldInput(props: IBaseFieldInputProps) {
    const width = props.width || props.widthMd || 12;

    return <>
        <div className={`${props.fieldClass || 'field'} col-${width} md:col-${props.widthMd || 12}`}>
            <InfoLabel htmlFor={`${props.property}`} info={props.info}>{props.label}</InfoLabel>
            {props.children}
        </div>
    </>
}

export function TextFieldInput(props: ITextFieldInputProps) {
    const onChange = (e: any) => {
        const change = {value: e.target.value, property: props.property} as InputChangeEvent<string>;
        props.onChange!!(change);
    }

    return <BaseFieldInput {...props}>
        <InputText id={`${props.property}`}
                   name={`${props.property}`}
                   placeholder={props.label}
                   value={props.value || props.default || ''}
                   onChange={onChange}
                   disabled={props.disabled}
        />
    </BaseFieldInput>
}

export function NumberFieldInput(props: INumberFieldInputProps) {
    return <BaseFieldInput {...props}>
        <InputNumber id={`${props.property}`}
                     name={`${props.property}`}
                     placeholder={props.label}
                     value={props.value || props.default || 0}
                     onValueChange={e => props.onChange!!({value: e.value!!, property: props.property})}
                     suffix={props.suffix}
                     disabled={props.disabled}
        />
    </BaseFieldInput>
}

export function ChipsFieldInput(props: IChipsFieldInputProps) {
    return <BaseFieldInput {...props}>
        <Chips id={`${props.property}`}
               name={`${props.property}`}
               placeholder={props.label}
               value={props.value || props.default || []}
               onChange={e => props.onChange!!({property: props.property, value: e.value!!})}
               disabled={props.disabled}
        />
    </BaseFieldInput>
}

export function BooleanFieldInput(props: IBooleanFieldInputProps) {
    const onChange = (e: any) => {
        console.log("check event, ", e)
        props.onChange!!({value: e.checked, property: props.property})
    }

    return <BaseFieldInput {...props} fieldClass="field-checkbox">
        <Checkbox id={`${props.property}`}
                  name={`${props.property}`}
                  checked={props.value}
                  onChange={onChange}
                  disabled={props.disabled}
                  className={`ml-2 ${props.fieldClass}`}
        />
    </BaseFieldInput>
}

export function DropdownFieldInput(props: IDropdownFieldInputProps) {
    return <BaseFieldInput {...props} fieldClass="field">
        <Dropdown id={`${props.property}`}
                  name={`${props.property}`}
                  placeholder={props.label}
                  value={props.value || props.default}
                  options={props.options}
                  onChange={e => props.onChange!!({property: props.property, value: e.value})}
                  editable={props.editable}
        />
    </BaseFieldInput>
}

export function DatasetDropdownFieldInput(props: IDatasetDropdownInputProps) {
    return <BaseFieldInput {...props} fieldClass="field">
        <DatasetDropdown value={props.value}
                         onChange={(e) => props.onChange!!({property: props.property, value: e})}
        />
    </BaseFieldInput>
}

export function DatasetMultiSelectFieldInput(props: IDatasetMultiSelect) {
    return <BaseFieldInput {...props} fieldClass="field">
        <DatasetMultiSelect value={props.value}
                            onChange={(e) => props.onChange!!({property: props.property, value: e})}/>
    </BaseFieldInput>
}

export function DatasetCategoryDropdownField(props: IDatasetCategoryDropdownInputProps) {
    return <BaseFieldInput {...props} fieldClass="field">
        <DatasetCategoryDropdown value={props.value}
                                 onChange={(e) => props.onChange!!({property: props.property, value: e})}
        />
    </BaseFieldInput>
}

export function TaskConfigProductTemplateField(props: ITaskConfigProductTemplateFieldProps) {
    const [editMode, setEditMode] = useState(false);
    const [editValue, setEditValue] = useState(props.value || new ProductTemplateTaskSelectionOptions());
    const toast = useRef<Toast>(null);

    const onCancelEdit = () => {
        setEditMode(false);
        setEditValue(props.value ?? new ProductTemplateTaskSelectionOptions());
    }

    const onEdit = () => {
        setEditMode(true);
    }

    const onApply = () => {
        props.onChange!!({property: props.property, value: editValue});
        setEditMode(false);
    }

    const dialogFooter = () => {
        return <div className="flex justify-content-between">
            <Button label="Apply" icon="pi pi-check" onClick={onApply} className="p-button-success p-button-text"/>
            <Button label="Cancel" icon="pi pi-times" onClick={onCancelEdit} className="p-button-danger p-button-text"/>
        </div>
    }

    const onChangeProdTemplate = (value: IProductTemplateDropdownChangeParams) => {
        const edit = {...editValue}
        edit.id = value.value;
        edit.productTemplateRef = value.refName;
        setEditValue(edit);
    }

    const onChangeValue = (value: InputChangeEvent<any>) => {
        setEditValue({...editValue, [value.property!!]: value.value});
    }

    return <div className={`field col-${props.width || 12} md:col-${props.widthMd || 12}`}>
        <InfoLabel htmlFor={`${props.property}`} info={props.info}>{props.label}</InfoLabel>
        <div className="flex align-content-center">
            {props.value?.id &&
                <span style={{padding: "0.75rem 1rem 0.75rem 0"}}>
                        <span style={{fontSize: "1.125rem"}}><ProductTemplateTitle value={props.value?.id!!}/></span>

                    {props.value?.entryDataset &&
                        <><Tag icon="pi pi-database" style={{marginLeft: "0.5rem"}}><DatasetTitle
                            identifier={props.value?.entryDataset!!}/></Tag></>
                    }

                    <Tag icon="pi pi-list" style={{marginLeft: "0.5rem"}}>{props.value?.latestEntryCount}</Tag>
                    </span>
            }

            {!props.value?.id &&
                <span style={{padding: "0.75rem 1rem 0.75rem 0"}}>Product Template not selected!</span>}

            {!editMode &&
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-text"
                        onClick={onEdit}
                />
            }
        </div>

        <Dialog header={"Product template input settings"}
                visible={editMode}
                onHide={onCancelEdit}
                style={{width: 'min(500px, 100%)'}}
                footer={dialogFooter}
        >
            <div className="p-fluid grid formgrid">
                <div className="field col-12">
                    <InfoLabel>Product template</InfoLabel>
                    <ProductTemplateDropdown value={editValue.id} onChange={onChangeProdTemplate}/>
                </div>

                <NumberFieldInput value={editValue.latestEntryCount} label={"Entry count"} property={"latestEntryCount"}
                                  widthMd={8} onChange={onChangeValue}/>
                <BooleanFieldInput value={editValue.useDataset} label={"Use dataset"} property="useDataset" widthMd={4}
                                   onChange={onChangeValue}/>

                {editValue.useDataset &&
                    <DatasetDropdownFieldInput value={editValue.entryDataset} label={"Entry dataset"}
                                               property="entryDataset" onChange={onChangeValue}/>
                }

            </div>
        </Dialog>

        <Toast ref={toast}/>
    </div>
}


export function TaskConfigFreestyleJsonField(props: ITaskConfigFreestyleJsonFieldProps) {
    const [editValue, setEditValue] = useState(() => props.value ? JSON.stringify(props.value, null, 2) : "");
    const [valid, setValid] = useState(true);
    const [syntaxError, setSyntaxError] = useState(null);

    const width = props.width || props.widthMd || 12;

    const updateValue = (objStr: string, obj: any) => {
        setEditValue(objStr);
        props.onChange!!({property: props.property, value: obj});
        setValid(true);
    }

    const onChange = (value: string | undefined, ev: any) => {
        if (!value) {
            updateValue("", null);
        }

        try {
            const json = JSON.parse(value!!);
            updateValue(value!!, json);
        } catch (e) {
            setValid(false);
        }
    }

    const handleEditorValidation = (markers: any[]) => {
        if (markers?.length > 0) {
            setSyntaxError(markers[0].message);
        }
    }

    return <div className={`field col-${width}`}>
        <InfoLabel info={props.info}>{props.label}</InfoLabel>

        {!valid &&
            <Tag icon="pi pi-exclamation-triangle" severity="danger" className="ml-2">{syntaxError}</Tag>
        }

        <Editor
            height="400px"
            value={editValue}
            onChange={onChange}
            onValidate={handleEditorValidation}
            defaultLanguage="json"
            theme="vs-dark"
        />
    </div>
}

export function TaskDependencyDropdown(props: IAirflowDepDropdownProps) {
    const [selections, setSelections] = useState<DropdownItem<string>[]>([])
    const query = useGetAirflowDagTaskInfos(props.dagId)

    useEffect(() => {
        if (query.data) {
            const options: DropdownItem<string>[] = query.data.map((task) => {
                return {label: task.taskTitle || `No Title [id:${task.taskId}]`, value: task.taskId}
            });

            setSelections(options)
        }
    }, [query.data]);

    const handleChange = (e: MultiSelectChangeParams) => {
        props.onChange({property: props.property, value: e.value})
    }

    return <BaseFieldInput {...props} fieldClass="field">
        <>
            {query.isLoading &&
                <div>Loading...</div>
            }

            {query.isError &&
                <div>Error!</div>
            }

            {query.isSuccess && query.data &&
                <MultiSelect id={`${props.property}`}
                             name={`${props.property}`}
                             placeholder={props.label}
                             value={props.value || []}
                             options={selections}
                             onChange={handleChange}

                />
            }
        </>
    </BaseFieldInput>
}

export function CodeUploadInput(props: IFileInputProps) {
    const [codeId, setCodeId] = useState(props.value);
    const [code, setCode] = useState(props.initialCode);
    const [codeBase64, setCodeBase64] = useState(props.codeB64);
    const [filename, setFilename] = useState(props.initialFilename);
    const [showCodeDialog, setShowCodeDialog] = useState(false);

    const toast = useRef<Toast>(null);
    const fileUploadRef = useRef<FileUpload>(null);

    const emptyTemplate = () => {
        return <p className="m-0">
            Drag and drop python files (.py or .zip) here to upload.<br/>
            {code &&
                <>Current file: {filename} {codeId}</>
            }
            {!code &&
                <>No file uploaded.</>
            }
        </p>
    }

    const propagateChange = (codeId: number | null, code: string | null, codeB64: string | null, filename: string | null) => {
        const change = {
            id: codeId,
            code: code,
            codeB64: codeB64,
            name: filename
        } as IPythonCodeFile

        props.onChange(change);
    }

    const showSuccess = (message: string) => {
        toast.current?.show({severity: 'success', summary: 'Success', detail: message});
    }

    const showError = (message: string) => {
        toast.current?.show({severity: 'error', summary: 'Error', detail: message});
    }

    const customUpload = async (e: FileUploadHandlerParam) => {
        const file = e.files[0];

        ScheduledTasksApi.uploadCodeFile(file)
            .then((r) => {
                if (!r.error && r.data) {
                    setCodeId(r.data.id);
                    setCode(r.data.code)
                    setFilename(r.data.name)
                    setCodeBase64(r.data.codeB64)
                    propagateChange(r.data.id, r.data.code, r.data.codeB64, r.data.name);
                    showSuccess('File Uploaded');

                    setTimeout(() => {
                        fileUploadRef.current?.clear(); // fixes second drag and drop not working
                    }, 2000);

                } else {
                    e.options.clear();
                    showError("File upload failed! " + r.error?.message)
                }
            });
    }

    const download = () => {
        if (codeBase64) {
            downloadBase64AsFile(codeBase64, filename!!)
            showSuccess('File downloaded');
        } else {
            showError('No code to download');
        }
    }

    const uploadHeaderTemplate = (options: FileUploadHeaderTemplateOptions) => {
        const {className, chooseButton} = options;

        return (
            <div className={className} style={{backgroundColor: 'transparent', display: 'flex', alignItems: 'center'}}>
                {chooseButton}
                {code &&
                    <>
                        <Button label="Show Code" icon="pi pi-eye" onClick={() => setShowCodeDialog(true)}/>
                        <Button label="Download code" icon="pi pi-download p-button-" onClick={download}/>
                    </>
                }
            </div>
        );
    }

    const itemTemplate = () => {
        return <div className="flex align-content-start">
            <p>Uploaded File: {filename}</p>
        </div>
    }

    const dialogFooter = () => {
        return <Button label="Close" icon="pi pi-times" onClick={() => setShowCodeDialog(false)}
                       className="p-button-text"/>
    }

    return <div className={`field col-12 md:col-12`}>
        <Toast ref={toast}/>

        <FileUpload id="pythonCode"
                    ref={fileUploadRef}
                    name="file"
                    mode="advanced"
                    multiple={false}
                    url={`${restApiRoot}/scheduled-tasks/code/upload`}
                    headerTemplate={uploadHeaderTemplate}
                    emptyTemplate={emptyTemplate}
                    itemTemplate={itemTemplate}
                    uploadHandler={customUpload}
                    customUpload auto
        />

        <Dialog header={`Python Code: ${filename}`}
                visible={showCodeDialog}
                style={{width: 'min(850px, 100%)'}}
                footer={dialogFooter}
                onHide={() => setShowCodeDialog(false)}
        >
            <Editor
                height="700px"
                value={code ?? ""}
                defaultLanguage="python"
                theme="vs-dark"
            />
        </Dialog>

    </div>
}

export function TimespanFieldInput(props: IChronoSpanInputProps) {
    return <BaseFieldInput {...props} fieldClass="field">
        <div className="flex">
            <InputNumber id={`${props.property}`}
                         name={`${props.property}`}
                         placeholder={props.label}
                         value={props.value || props.default || 0}
                         onValueChange={e => props.onChange!!({property: props.property, value: e.value!!})}
                         disabled={props.disabled}
            />

            <Dropdown id={`${props.propertyUnit}`}
                      name={`${props.propertyUnit}`}
                      placeholder="Unit"
                      value={props.valueUnit || props.defaultUnit}
                      options={chronoUnits}
                      onChange={e => props.onChange!!({property: props.propertyUnit, value: e.value!!})}
                      disabled={props.disabled}
                      style={{width: "12rem"}}
            />
        </div>
    </BaseFieldInput>
}

export function TaskConfigConstantField(props: ITaskConfigConstantFieldProps) {
    const {options, value, onChange} = props;
    // const defaultValue = options?.default;

    if (!options) { // DEFAULT FIELD (string)
        return <TextFieldInput {...props}
            // default={defaultValue}
        />
    }

    const selectionOptionsExist = options.selectOptions && options.selectOptions.length > 0;

    function convertStringValueToOtherType(value: any, convertTo: TaskTemplateInputSubType) {
        if (typeof value === "string") {
            if (convertTo === TaskTemplateInputSubType.BOOLEAN) {
                return JSON.parse(value)
            }
            if (convertTo === TaskTemplateInputSubType.NUMBER) {
                return Number(value)
            }
        }
        return value
    }

    function convertToString(value: any) {
        return value ? String(value) : undefined;
    }

    function convertArrayToStringArray(arr?: Array<any>) {
        return arr?.map(convertToString);
    }

    const onValueChange = (value: any) => {
        if (onChange) onChange({value: convertToString(value.value), property: value.property})
    }

    if (options.subType) {
        if (selectionOptionsExist) { // Dropdowns
                return <DropdownFieldInput {...props}
                                           value={value}
                                           options={convertArrayToStringArray(options.selectOptions)}
                                           onChange={onValueChange}
                                           editable={options.subType === TaskTemplateInputSubType.STRING && options.selectionsOptional} // JUST STRING
                />
        } else { // Simple fields
            if (options.subType === TaskTemplateInputSubType.STRING) {
                return <TextFieldInput {...props}
                                       // default={convertToString(defaultValue)}
                />
            } else if (options.subType === TaskTemplateInputSubType.NUMBER) {
                return <NumberFieldInput {...props}
                                         value={convertStringValueToOtherType(value, "NUMBER" as TaskTemplateInputSubType)}
                                         suffix={" " + options.unit}
                />
            } else if (options.subType === TaskTemplateInputSubType.BOOLEAN) {
                return <BooleanFieldInput {...props}
                                          value={convertStringValueToOtherType(value, "BOOLEAN" as TaskTemplateInputSubType)}
                />
            } else {
                return <BaseFieldInput {...props} fieldClass="field">
                    <div>SubType "{`${options.subType}`}" does not exist</div>
                </BaseFieldInput>
            }
        }
    }

    return <BaseFieldInput {...props} fieldClass="field">
        <div>Error in configuration</div>
    </BaseFieldInput>
}




